import { StoreState } from '@/core/models/store.interface';
import i18n from '@/i18n';
import { Module } from 'vuex';
import { TagType } from '../tag-type/tag-type.interface';
import { Tag } from '../tag/tag.interface';
import { Pool } from './pool.interface';
import poolService from './pool.service';

export interface PoolState {
   pool?: Pool;
   pools?: Array<Pool>;
}

const pool: Module<PoolState, StoreState> = {
   namespaced: true,
   state: {
      pool: undefined,
      pools: [],
   },
   getters: {},
   mutations: {
      setPools(state: PoolState, pools) {
         state.pools = pools;
      },
      UPDATE_POOL_COMPANY_AND_TEAM_AND_CONTRACT_DEFAULT_USER(
         state: PoolState,
         { contract_team, company, contract_creation_type, contract_default_user },
      ) {
         if (!state.pool) return;
         state.pool['company'] = company;
         state.pool['contract_team'] = contract_team;
         state.pool['contract_team_id'] = contract_team?.id;
         state.pool['contract_default_user'] = contract_default_user;
         state.pool['contract_default_user_id'] = contract_default_user?.id;
         state.pool['contract_creation_type'] = contract_creation_type;
         state.pool['contract_creation_type_id'] = contract_creation_type?.id;
      },
      setPoolPermission(state: PoolState, { accessor_type, accessor_id, is_allowed, scope, accessor_name, accessor_tenant }) {
         const permission_to_change = state.pool?.permissions?.find(
            (p) => p.accessor.type == accessor_type && p.accessor.id == accessor_id && p.scope.key == scope.key,
         );
         if (permission_to_change && is_allowed != null) {
            permission_to_change.is_allowed = is_allowed;
         } else if (is_allowed != null) {
            state.pool?.permissions?.push({
               accessor: { id: accessor_id, type: accessor_type, name: accessor_name, tenant: accessor_tenant },
               is_allowed: is_allowed,
               scope: scope,
            });
         } else {
            const INDEX = state.pool?.permissions?.findIndex(
               (p) => p.accessor.type == accessor_type && p.accessor.id == accessor_id && p.scope.key == scope.key,
            );
            if (INDEX && INDEX >= 0) state.pool?.permissions?.splice(INDEX, 1);
         }
      },
      setPoolPermissions(state: PoolState, { permissions }) {
         if (state.pool) state.pool.permissions = permissions;
      },
      removeTagTypeFromPool(state: PoolState, tag_type_id) {
         if (state.pool) state.pool.tag_types = state.pool.tag_types?.filter((tt) => tt.id != tag_type_id);
      },
      patchTagTypeInPool(state: PoolState, { tag_type_id, path, value }: { tag_type_id: number; path: keyof TagType; value: never }) {
         state.pool?.tags?.forEach((t) => {
            if (t.tag_type?.id == tag_type_id) {
               if (t.tag_type) t.tag_type[path] = value;
            }
         });
      },
      UPDATE_TAG_TYPE(state: PoolState, tag_type) {
         if (!state.pool) return;
         const TAG_TYPE_INDEX = state.pool.tag_types?.findIndex((tt) => tt.id == tag_type.id);
         if (TAG_TYPE_INDEX && state.pool?.tag_types) state.pool.tag_types[TAG_TYPE_INDEX] = tag_type;
      },
      removeTagFromPool(state: PoolState, { tag_id, tag_type_id }) {
         if (state.pool) {
            const tag_type_index = (state.pool.tag_types || [])?.findIndex((tt) => tt.id == tag_type_id);
            if (tag_type_index === -1) return;

            const tag_index = state.pool.tag_types?.[tag_type_index].tags.findIndex((t) => t.id == tag_id);

            if (tag_index && tag_index !== -1) state.pool.tag_types?.[tag_type_index].tags.splice(tag_index, 1);
         }
      },
      patchTagInPool(state: PoolState, { tag_id, tag_type, path, value }) {
         if (state.pool) {
            const tags = state.pool.tag_types?.find((tt) => tt.id == tag_type.id)?.tags || [];
            const tag = tags.find((t) => t.id == tag_id);
            if (!tag) return;
            tags[path] = value;
         }
      },
      addTagToPool(state: PoolState, tag: Tag) {
         if (state.pool) {
            state.pool.tag_types?.find((tt) => tt.id == tag.tag_type_id)?.tags.push(tag);
         }
      },
      linkTagType(state: PoolState, tag_type) {
         if (state.pool) {
            if ((state.pool.tag_types || []).length <= 0) state.pool.tag_types = [];
            state.pool.tag_types!.push(tag_type);
         }
      },
   },

   actions: {
      async getFilteredByTenant(context, { name, page, tenant_id, cancelToken }) {
         context.commit('setPools', null);
         return await poolService.getFilteredByTenant({ name, page, tenant_id, cancelToken }).then((response) => {
            context.commit('setPools', response.data);
            return response;
         });
      },
      async postNotification(
         context,
         {
            pool_id,
            medium_type,
            type,
            message,
            start_date,
            end_date,
            language,
            candidate_ids,
         }: {
            pool_id: number;
            medium_type: string;
            type: string;
            message: string;
            start_date: string;
            end_date: string;
            language: string;
            candidate_ids: Array<number>;
         },
      ) {
         const BODY = { medium_type, type, message, start_date, end_date, candidate_ids };

         return await poolService.postNotification(pool_id, BODY).then((response) => {
            if (response.messages) {
               if (candidate_ids.length > (response?.messages.length ?? 0)) {
                  context.dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: 'success',
                           message: `${i18n.global.t('pool.free_message_sent', { language: language })}`,
                        },
                     },
                     { root: true },
                  );
               }

               response.messages.forEach((message: string) => {
                  context.dispatch(
                     'addNotification',
                     {
                        notification: {
                           type: 'error',
                           message: message,
                        },
                     },
                     { root: true },
                  );
               });
            } else {
               context.dispatch(
                  'addNotification',
                  {
                     notification: {
                        type: 'success',
                        message: `${i18n.global.t('pool.free_message_sent', { language: language })}`,
                     },
                  },
                  { root: true },
               );
            }

            return response;
         });
      },
      async postCandidatePoolTagsBulk(context, { pool_id, candidate_pool_ids, tags }) {
         return await poolService.postCandidatePoolTagsBulk({ pool_id, body: { candidate_pool_ids, tags } }).then((response) => {
            context.commit('candidatePool/UPDATE_CANDIDATE_POOLS', response, { root: true });
            return response;
         });
      },
   },
};

export default pool;
